import { memo, useState } from 'react';
import { clsx } from 'clsx';
import { IconButton, Icon, Tooltip, Divider, Popover, icon } from '@nula/core-ui';
import type { FormattingState } from '@/features/canvas/engine/hooks/use-formatting-state';
import type { NodeType } from '@/features/canvas/utils/node-types';
import { SHAPE_COMPONENTS, type ShapeKind } from '@/features/canvas/engine/nodes/shape/shapes';
import { ColorSwatchPopover } from '../../primitives/formatting/color-swatch-popover';
import { FontGroup, TextStyleGroup, AlignGroup, FillBorderGroup } from '../../primitives/formatting';
import styles from './node-floating-toolbar.module.css';

// FontGroup, TextStyleGroup, AlignGroup, and FillBorderGroup were
// extracted to ./formatting/* so FrameFormattingToolbar can compose the
// same primitives. Only ShapeKindPicker stays here — shape-specific.

// ── Shape kind picker ──────────────────────────────────────────────────────

const SHAPE_KIND_LIST = Object.keys(SHAPE_COMPONENTS) as ShapeKind[];

const SHAPE_LABELS: Record<ShapeKind, string> = {
  rectangle:    'Rectangle',
  pill:         'Pill',
  ellipse:      'Ellipse',
  diamond:      'Diamond',
  triangle:     'Triangle',
  star:         'Star',
  pentagon:     'Pentagon',
  hexagon:      'Hexagon',
  parallelogram:'Parallelogram',
  chevron:      'Chevron',
  cross:        'Cross',
  cylinder:     'Cylinder',
  'arrow-shape':'Arrow',
};

// Memoised: ShapeKindPicker renders 12 of these in its grid; without memo,
// every re-render of the parent NodeFormattingToolbar walks all 12 even
// though the only input that ever changes is shapeKind itself. Each render
// instantiates a new ShapeVariant React element.
const ShapeSvgIcon = memo(function ShapeSvgIcon({ shapeKind }: { shapeKind: ShapeKind }) {
  const ShapeVariant = SHAPE_COMPONENTS[shapeKind];
  // Pill is dimension-aware: render it wide in the icon (100×44) so it reads
  // as a pill, not the square-box circle the dimensionless fallback produces.
  // Pill: draw a plain wide pill rect directly (a wide rect with rx = half its
  // height). The dimension-compensating variant is for the stretched node
  // context; in a static icon a direct rect reads correctly and simply.
  if (shapeKind === 'pill') {
    return (
      <svg viewBox="0 0 100 100" width={icon.md} height={icon.md} className={styles.shapePickerIcon}>
        <rect x="6" y="32" width="88" height="36" rx="18" ry="18" vectorEffect="non-scaling-stroke" />
      </svg>
    );
  }
  return (
    <svg viewBox="-8 -8 116 116" width={icon.md} height={icon.md} className={styles.shapePickerIcon}>
      <g vectorEffect="non-scaling-stroke">
        <ShapeVariant cornerRadius={0} strokeDasharray={undefined} />
      </g>
    </svg>
  );
});

interface ShapeKindPickerProps {
  state: FormattingState;
}

// Memoised: NodeFormattingToolbar renders on every formatting state change
// (font size, color, etc) — but ShapeKindPicker only depends on shapeKind +
// setShapeKind. Memoising avoids walking the 12-shape grid on every keystroke
// and color tweak.
const ShapeKindPicker = memo(function ShapeKindPicker({ state }: ShapeKindPickerProps) {
  const [open, setOpen] = useState(false);
  const current = state.shapeKind ?? 'rectangle';

  const content = (
    <div className={styles.shapeGrid}>
      {SHAPE_KIND_LIST.map((kind) => (
        <Tooltip key={kind} title={SHAPE_LABELS[kind]} placement="top">
          <IconButton
            transparent
            aria-label={SHAPE_LABELS[kind]}
            aria-pressed={current === kind}
            className={clsx(styles.btn, current === kind && styles.shapePickerActive)}
            onClick={() => { state.setShapeKind(kind); setOpen(false); }}
          >
            <ShapeSvgIcon shapeKind={kind} />
          </IconButton>
        </Tooltip>
      ))}
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="top" open={open} onOpenChange={setOpen}>
      <Tooltip title="Change shape" placement="top" open={open ? false : undefined}>
        <IconButton
          transparent
          aria-label="Change shape"
          aria-expanded={open}
          className={styles.btn}
        >
          <ShapeSvgIcon shapeKind={current} />
        </IconButton>
      </Tooltip>
    </Popover>
  );
});

// ── Freedraw formatting ────────────────────────────────────────────────────

export interface FreedrawState {
  strokeColor: string;
  strokeWidth: number;
  setStrokeColor: (color: string) => void;
  setStrokeWidth: (width: number) => void;
}

const FREEDRAW_SIZES_CTX = [
  { label: 'Thin',   sizes: [12, 22] },
  { label: 'Medium', sizes: [16, 34] },
  { label: 'Thick',  sizes: [26, 58] },
] as const;

function ThicknessPopover({ state }: { state: FreedrawState }) {
  const [open, setOpen] = useState(false);

  const content = (
    <div className={styles.styleGroup}>
      {FREEDRAW_SIZES_CTX.map(({ label, sizes }, i) => {
        const waveStroke = i === 0 ? 1.5 : i === 1 ? 2.5 : 4.5;
        return (
          <Tooltip key={label} title={label} placement="top">
            <IconButton
              transparent
              aria-label={label}
              className={styles.btn}
              onClick={() => { state.setStrokeWidth(sizes[0]); setOpen(false); }}
            >
              <svg width="18" height="8" viewBox="0 0 18 8" fill="none">
                <path
                  d="M1 5 C3 1, 6 1, 9 4 C12 7, 15 7, 17 4"
                  stroke="currentColor"
                  strokeWidth={waveStroke}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </IconButton>
          </Tooltip>
        );
      })}
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="top" open={open} onOpenChange={setOpen}>
      <Tooltip title="Line style" placement="top" open={open ? false : undefined}>
        <IconButton transparent aria-label="Line style" aria-expanded={open} className={styles.btn}>
          <Icon name="line_weight" size={icon.md} />
        </IconButton>
      </Tooltip>
    </Popover>
  );
}

interface FreedrawFormattingToolbarProps {
  state: FreedrawState;
}

export function FreedrawFormattingToolbar({ state }: FreedrawFormattingToolbarProps) {
  return (
    <>
      <ColorSwatchPopover
        label="Color"
        value={state.strokeColor}
        onChange={state.setStrokeColor}
      />
      <ThicknessPopover state={state} />
    </>
  );
}

// ── Main export ────────────────────────────────────────────────────────────

export interface NodeFormattingToolbarProps {
  nodeType: NodeType;
  state: FormattingState;
}

/**
 * Formatting controls fragment — no wrapper div. Intended to be inlined inside
 * NodeFloatingToolbar so the two share a single role="toolbar" root.
 *
 * Layout: [Shape▾] [Fill&Border] | [A-] [A+] | [Style▾] | [Align▾] | [T color]
 */
export function NodeFormattingToolbar({ nodeType, state }: NodeFormattingToolbarProps) {
  return (
    <>
      {/* Group 1 — shape / color (shape + sticky only) */}
      {nodeType === 'shape' && <ShapeKindPicker state={state} />}
      {(nodeType === 'shape' || nodeType === 'sticky') && <FillBorderGroup state={state} />}

      {(nodeType === 'shape' || nodeType === 'sticky') && (
        <Divider orientation="vertical" className={styles.dividerMajor} />
      )}

      {/* Group 2 — font */}
      <FontGroup state={state} />
      <TextStyleGroup state={state} />
      <AlignGroup state={state} />
      <ColorSwatchPopover label="Text color" value={state.textColor} onChange={state.setTextColor} />
    </>
  );
}
